/**
 * Voice section structure: 5 top-level sidebar items + Settings, each hosting the
 * old standalone Voice pages as tabs. Every tab is still its own route, so existing
 * URLs, deep links and per-route RBAC keep working.
 *
 * Consumed by `navData.tsx` (sidebar), `VoiceSectionTabs` (tab bar) and
 * `routePathPermissions.ts` (route guards, via the nav `routes`).
 */

export type VoiceTab = {
  label: string;
  path: string;
  /** Header/breadcrumb title for the page (the page's old sidebar name). */
  pageTitle: string;
  permission: string;
  /** Extra routes that belong to this tab (highlight it, but are not tabs themselves). */
  aliasPaths?: string[];
};

export type VoiceSection = {
  key: string;
  title: string;
  /** Shared page header shown above the tab bar; pages in this section then omit their own title. */
  header?: { title: string; subtitle: string };
  tabs: VoiceTab[];
};

export const VOICE_SECTIONS: VoiceSection[] = [
  {
    key: 'overview',
    title: 'Overview',
    tabs: [{ label: 'Performance', path: '/voice-analytics', pageTitle: 'Voice Analytics', permission: 'voice.analytics.view' }],
  },
  {
    key: 'agents',
    title: 'Agents',
    tabs: [
      { label: 'Setup', path: '/voice-agents', pageTitle: 'Voice Agents', permission: 'voice.agent.view' },
      { label: 'Knowledge', path: '/knowledge-base', pageTitle: 'Knowledge Base', permission: 'voice.agent.view' },
      { label: 'Workflows', path: '/voice-workflows', pageTitle: 'Voice Workflows', permission: 'voice.workflow.view' },
      { label: 'Intents', path: '/voice-intents', pageTitle: 'Session Intents', permission: 'campaign.view' },
    ],
  },
  {
    key: 'campaigns',
    title: 'Campaigns',
    tabs: [
      { label: 'All campaigns', path: '/voice-campaigns', pageTitle: 'Voice Campaigns', permission: 'voice.campaign.view' },
      { label: 'Schedule', path: '/voice-outbound', pageTitle: 'Outbound Scheduler', permission: 'voice.campaign.view' },
      { label: 'Missed call recovery', path: '/voice-recovery', pageTitle: 'Missed Call Recovery', permission: 'voice.recovery.view' },
    ],
  },
  {
    key: 'calls',
    title: 'Calls',
    header: {
      title: 'Calls',
      subtitle: 'Every call your agents handle, plus anything waiting on you.',
    },
    tabs: [
      { label: 'Live', path: '/voice-supervisor', pageTitle: 'Supervisor', permission: 'voice.supervisor.view' },
      { label: 'All calls', path: '/conversations', pageTitle: 'Conversations', permission: 'voice.agent.view' },
      { label: 'Voicemails', path: '/voice-voicemails', pageTitle: 'Voicemails', permission: 'voice.voicemail.view' },
      { label: 'Callbacks', path: '/voice-callbacks', pageTitle: 'Callbacks', permission: 'voice.callback.view' },
    ],
  },
  {
    key: 'contacts',
    title: 'Contacts',
    tabs: [
      { label: 'All contacts', path: '/voice-contacts', pageTitle: 'Contacts', permission: 'voice.contact.view' },
      { label: 'Segments', path: '/voice-segments', pageTitle: 'Saved Segments', permission: 'voice.segment.view' },
    ],
  },
  {
    key: 'settings',
    title: 'Settings',
    tabs: [
      { label: 'General', path: '/voice-settings', pageTitle: 'Voice Settings', permission: 'voice.settings.view' },
      { label: 'Phone numbers', path: '/voice-phone-numbers', pageTitle: 'Phone Numbers', permission: 'voice.phone_number.view' },
      { label: 'Integrations', path: '/voice-webhooks', pageTitle: 'Webhook Subscriptions', permission: 'voice.webhook.view' },
      { label: 'Plan', path: '/account/voice-subscription', pageTitle: 'Voice Subscription', permission: 'voice.subscription.view' },
      { label: 'Billing', path: '/account/voice-billing', pageTitle: 'Voice Billing', permission: 'voice.billing.view' },
    ],
  },
];

const stripTrailingSlash = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);

/** Section whose tab bar applies to `pathname`, only on a tab's own list page (not create/detail sub-pages). */
export function getVoiceSectionForTabBar(pathname: string): VoiceSection | null {
  const p = stripTrailingSlash(pathname || '');
  return (
    VOICE_SECTIONS.find((s) =>
      s.tabs.some((t) => t.path === p || t.aliasPaths?.includes(p))
    ) ?? null
  );
}
